import cantonsFr from './cantonal-guide.data.fr'
import cantonsEn from './cantonal-guide.data.en'
import cantonsDe from './cantonal-guide.data.de'
import cantonsIt from './cantonal-guide.data.it'

const datasets = {
    fr: cantonsFr,
    en: cantonsEn,
    de: cantonsDe,
    it: cantonsIt
}

const tagMap = { ref: 'tag-ref', loi: 'tag-loi', ord: 'tag-ord', info: 'tag-loi' }
const tagLabels = {
    fr: { ref: 'Référendum', loi: 'Loi', ord: 'Ordonnance', info: 'Info' },
    en: { ref: 'Referendum', loi: 'Law', ord: 'Regulation', info: 'Info' },
    de: { ref: 'Referendum', loi: 'Gesetz', ord: 'Verordnung', info: 'Info' },
    it: { ref: 'Referendum', loi: 'Legge', ord: 'Ordinanza', info: 'Info' }
}
const statusMap = { strict: 'status-strict', moderate: 'status-moderate', progres: 'status-progres', federal: 'status-federal', voted: 'status-voted' }

// Fuel translations for each language
const fuelLabels = {
    fr: {
        oil: '🛢️ Mazout',
        gas: '🔥 Gaz de ville',
        propane: '⚗️ Propane / GPL',
        electric: '⚡ Élec. résistance'
    },
    en: {
        oil: '🛢️ Heating oil',
        gas: '🔥 Natural gas',
        propane: '⚗️ Propane / LPG',
        electric: '⚡ Electric resistance'
    },
    de: {
        oil: '🛢️ Heizöl',
        gas: '🔥 Erdgas',
        propane: '⚗️ Propan / Flüssiggas',
        electric: '⚡ Elektro-Widerstandsheizung'
    },
    it: {
        oil: '🛢️ Olio combustibile',
        gas: '🔥 Gas di città',
        propane: '⚗️ Propano / GPL',
        electric: '⚡ Elettrico a resistenza'
    }
}

const uiText = {
    fr: {
        cantonPrefix: 'Canton de',
        summaryTitle: 'Résumé de la situation',
        timelineTitle: 'Chronologie des mesures',
        legendPast: 'Mesure passée / en vigueur',
        legendNow: 'Situation actuelle',
        legendFuture: 'Mesure future',
        legendHalted: 'Suspendu / référendum',
        rulesTitle: 'Règles par combustible',
        fuelHeader: 'Combustible',
        ruleHeader: 'Règle applicable',
        subsidiesTitle: 'Subventions disponibles',
        federalTitle: 'Rappel fédéral (valable tous cantons) :',
        federalText: 'Taxe CO₂ de 120 CHF/t sur les combustibles fossiles (mazout, gaz). Montant redistribué partiellement à la population (~64 CHF/personne/an en 2024). Programme Bâtiments fédéral disponible partout. MoPEC 2025 adopté le 29 août 2025 — transposition cantonale en cours.',
        sourcePrefix: '📎 Source :',
        sourceSuffix: 'Données arrêtées au 24 avril 2026 · Les informations sont données à titre indicatif — consultez les autorités cantonales pour les cas individuels.',
        referendumTitle: '⚠️ Procédure référendaire',
        referendumParty: 'Parti / Comité :',
        referendumLaunch: 'Lancement :',
        referendumStatus: 'Résultat / Statut :',
        referendumSignatures: 'Signatures requises :',
        emptySelection: 'Veuillez sélectionner un canton.'
    },
    en: {
        cantonPrefix: 'Canton of',
        summaryTitle: 'Situation summary',
        timelineTitle: 'Timeline of measures',
        legendPast: 'Past / active measure',
        legendNow: 'Current situation',
        legendFuture: 'Future measure',
        legendHalted: 'Suspended / referendum',
        rulesTitle: 'Rules by fuel type',
        fuelHeader: 'Fuel type',
        ruleHeader: 'Applicable rule',
        subsidiesTitle: 'Available subsidies',
        federalTitle: 'Federal reminder (valid in all cantons):',
        federalText: 'CO₂ tax of CHF 120/t on fossil fuels (oil, gas). Amount partly redistributed to the population (~CHF 64/person/year in 2024). Federal Buildings Programme available everywhere. MoPEC 2025 adopted on 29 August 2025 — cantonal transposition in progress.',
        sourcePrefix: '📎 Source:',
        sourceSuffix: 'Data current as of 24 April 2026 · The information is provided for guidance only — please consult the cantonal authorities for individual cases.',
        referendumTitle: '⚠️ Referendum procedure',
        referendumParty: 'Party / Committee:',
        referendumLaunch: 'Launch:',
        referendumStatus: 'Result / Status:',
        referendumSignatures: 'Required signatures:',
        emptySelection: 'Please select a canton.'
    },
    de: {
        cantonPrefix: 'Kanton',
        summaryTitle: 'Zusammenfassung der Situation',
        timelineTitle: 'Chronologie der Massnahmen',
        legendPast: 'Vergangene / geltende Massnahme',
        legendNow: 'Aktuelle Situation',
        legendFuture: 'Zukünftige Massnahme',
        legendHalted: 'Ausgesetzt / Referendum',
        rulesTitle: 'Regeln nach Energieträger',
        fuelHeader: 'Energieträger',
        ruleHeader: 'Geltende Regel',
        subsidiesTitle: 'Verfügbare Förderungen',
        federalTitle: 'Hinweis Bund (gültig in allen Kantonen):',
        federalText: 'CO₂-Abgabe von 120 CHF/t auf fossile Brennstoffe (Heizöl, Gas). Betrag wird teilweise an die Bevölkerung zurückverteilt (~64 CHF/Person/Jahr im Jahr 2024). Bundesprogramm Gebäude ist überall verfügbar. MoPEC 2025 wurde am 29. August 2025 verabschiedet — die Umsetzung in den Kantonen läuft.',
        sourcePrefix: '📎 Quelle:',
        sourceSuffix: 'Datenstand 24. April 2026 · Die Informationen dienen nur zur Orientierung — bitte konsultieren Sie die kantonalen Behörden für Einzelfälle.',
        referendumTitle: '⚠️ Referendumsverfahren',
        referendumParty: 'Partei / Komitee:',
        referendumLaunch: 'Start:',
        referendumStatus: 'Ergebnis / Status:',
        referendumSignatures: 'Erforderliche Unterschriften:',
        emptySelection: 'Bitte wählen Sie einen Kanton aus.'
    },
    it: {
        cantonPrefix: 'Cantone di',
        summaryTitle: 'Sintesi della situazione',
        timelineTitle: 'Cronologia delle misure',
        legendPast: 'Misura passata / in vigore',
        legendNow: 'Situazione attuale',
        legendFuture: 'Misura futura',
        legendHalted: 'Sospeso / referendum',
        rulesTitle: 'Regole per tipo di combustibile',
        fuelHeader: 'Combustibile',
        ruleHeader: 'Regola applicabile',
        subsidiesTitle: 'Sovvenzioni disponibili',
        federalTitle: 'Promemoria federale (valido in tutti i cantoni):',
        federalText: 'Tassa CO₂ di 120 CHF/t sui combustibili fossili (olio combustibile, gas). Importo parzialmente ridistribuito alla popolazione (~64 CHF/persona/anno nel 2024). Programma Edifici federale disponibile ovunque. MoPEC 2025 adottato il 29 agosto 2025 — trasposizione cantonale in corso.',
        sourcePrefix: '📎 Fonte:',
        sourceSuffix: 'Dati aggiornati al 24 aprile 2026 · Le informazioni sono fornite a titolo indicativo — consultate le autorità cantonali per i singoli casi.',
        referendumTitle: '⚠️ Procedura referendaria',
        referendumParty: 'Partito / Comitato:',
        referendumLaunch: 'Avvio:',
        referendumStatus: 'Risultato / Stato:',
        referendumSignatures: 'Firme richieste:',
        emptySelection: 'Selezionate un cantone.'
    }
}

function getCurrentGuideLanguage() {
    return (document.documentElement.lang || 'fr').split('-')[0].toLowerCase()
}

function renderCanton(code) {
    const currentLanguage = getCurrentGuideLanguage()
    const cantons = datasets[currentLanguage] || datasets.fr
    const labels = tagLabels[currentLanguage] || tagLabels.fr
    const texts = uiText[currentLanguage] || uiText.fr
    const fuels = fuelLabels[currentLanguage] || fuelLabels.fr
    const canton = cantons[code]
    if (!canton) return ''

    const timeline = (canton.timeline || []).map((item) => `
        <div class="tl-item tl-${item.type}">
            <div class="tl-date">${item.date} <span class="tl-tag ${tagMap[item.tag]}">${labels[item.tag]}</span></div>
            <div class="tl-text">${item.text}</div>
        </div>
    `).join('')

    const referendum = canton.referendum ? `
        <div class="cantonal-guide__section">
            <div class="cantonal-guide__section-title">${texts.referendumTitle}</div>
            <div class="alert-box alert-red cantonal-guide__alert-box">
                <strong>${texts.referendumParty}</strong> ${canton.referendum.parti}<br>
                <strong>${texts.referendumLaunch}</strong> ${canton.referendum.date_lancement}<br>
                <strong>${texts.referendumStatus}</strong> ${canton.referendum.votation}
                ${canton.referendum.signatures ? `<br><strong>${texts.referendumSignatures}</strong> ${canton.referendum.signatures}` : ''}
            </div>
        </div>
    ` : ''

    return `
        <div class="canton-header">
            <div class="canton-flag">${canton.flag}</div>
            <div class="canton-meta">
                <h2>${texts.cantonPrefix} ${canton.name} (${code})</h2>
                <p>${canton.region}</p>
                <div class="status-badge ${statusMap[canton.status]}">${canton.statusLabel}</div>
            </div>
        </div>

        <div class="cantonal-guide__section">
            <div class="cantonal-guide__section-title">${texts.summaryTitle}</div>
            <p class="cantonal-guide__summary">${canton.summary}</p>
        </div>

        <div class="cantonal-guide__section">
            <div class="cantonal-guide__section-title">${texts.timelineTitle}</div>
            <div class="timeline cantonal-guide__timeline">${timeline}</div>
            <div class="legend cantonal-guide__legend">
                <div class="legend-item"><div class="legend-dot legend-dot--past"></div> <span>${texts.legendPast}</span></div>
                <div class="legend-item"><div class="legend-dot legend-dot--now"></div> <span>${texts.legendNow}</span></div>
                <div class="legend-item"><div class="legend-dot legend-dot--future"></div> <span>${texts.legendFuture}</span></div>
                <div class="legend-item"><div class="legend-dot legend-dot--halted"></div> <span>${texts.legendHalted}</span></div>
            </div>
        </div>

        <div class="cantonal-guide__section">
            <div class="cantonal-guide__section-title">${texts.rulesTitle}</div>
            <table class="data-table cantonal-guide__data-table">
                <thead>
                    <tr>
                        <th class="cantonal-guide__fuel-column">${texts.fuelHeader}</th>
                        <th>${texts.ruleHeader}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr><td><strong>${fuels.oil}</strong></td><td>${canton.mazout}</td></tr>
                    <tr><td><strong>${fuels.gas}</strong></td><td>${canton.gaz}</td></tr>
                    <tr><td><strong>${fuels.propane}</strong></td><td>${canton.propane}</td></tr>
                    <tr><td><strong>${fuels.electric}</strong></td><td>${canton.elec}</td></tr>
                </tbody>
            </table>
        </div>

        ${referendum}

        <div class="cantonal-guide__section">
            <div class="cantonal-guide__section-title">${texts.subsidiesTitle}</div>
            <div class="alert-box alert-green cantonal-guide__alert-box">${canton.subventions}</div>
        </div>

        <div class="alert-box alert-blue cantonal-guide__alert-box cantonal-guide__alert-box--flush">
            <strong>${texts.federalTitle}</strong> ${texts.federalText}
        </div>

        <div class="source-bar cantonal-guide__source-bar">
            ${texts.sourcePrefix} ${canton.source} · ${texts.sourceSuffix}
        </div>
    `
}

export default function initCantonalGuide() {
    const container = document.querySelector('.cantonal-guide')
    if (!container) return

    const select = container.querySelector('#cantonSelect')
    const trigger = container.querySelector('.js-show-canton')
    const result = container.querySelector('#result')

    if (!select || !trigger || !result) return

    trigger.addEventListener('click', () => {
        const code = select.value

        if (!code) {
            const texts = uiText[getCurrentGuideLanguage()] || uiText.fr
            window.alert(texts.emptySelection)
            return
        }

        result.innerHTML = renderCanton(code)
        result.style.display = 'block'
        result.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
}